import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { formatNumber, pnlClass } from '@/utils/format';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { ClientLoginInfoTooltip, LpLoginInfoTooltip } from './tooltips';
import type {
  LpHubAccountDto,
  RiskDashboardClientAccountEnrichmentDto,
  RiskDashboardClientAccountRowDto,
  RiskDashboardLoginDetailDto,
  RiskDashboardLpAccountRowDto,
} from '@/api/types/riskDashboard';

function num(value: number | null | undefined, digits = 2): string {
  return value == null || Number.isNaN(value) ? 'N/A' : formatNumber(value, digits);
}
function pnl(value: number | null | undefined): ReactNode {
  if (value == null || Number.isNaN(value)) return 'N/A';
  return <span className={pnlClass(value)}>{formatNumber(value, 2)}</span>;
}

// ---------------------------------------------------------------------------
// Client accounts (REST enrichment items)
// ---------------------------------------------------------------------------

// Overlay the live NOP client-account rows onto the REST (Client Profitability)
// enrichment, keyed by "{platformId}:{login}" (mirrors _mergeNopWithCp in
// risk-dashboard-accounts.js). When an instrument filter is set the enrichment list
// is not instrument-scoped, so the merged list is pruned to logins actually present
// in the (already instrument-scoped) NOP live data.
export function mergeClientAccounts(
  enrichment: RiskDashboardClientAccountEnrichmentDto[],
  nop: RiskDashboardClientAccountRowDto[],
  hasInstrumentFilter: boolean,
): RiskDashboardClientAccountEnrichmentDto[] {
  const merged = new Map<string, RiskDashboardClientAccountEnrichmentDto>();
  enrichment.forEach((row) => merged.set(row.id, { ...row }));

  const nopKeys = new Set<string>();
  nop.forEach((row) => {
    const key = row.id || `${row.platformId ?? 0}:${row.login}`;
    nopKeys.add(key);
    const enrich = merged.get(key);
    if (enrich) {
      merged.set(key, {
        ...enrich,
        // Live overlay: the hub DTO (RiskDashboardClientAccountRowDto) only carries
        // floatingPnL / uniqueId / connector / netVolume / baseNotional — scalper,
        // drawdown and the enrichment metrics stay from the REST row (the live NOP
        // DTO does not send them in the migration).
        floatingPnL: row.floatingPnL != null ? row.floatingPnL : enrich.floatingPnL,
        uniqueId: row.uniqueId || enrich.uniqueId,
        connector: row.connector || enrich.connector,
      });
    } else {
      // Live-only login (no enrichment yet): synthesize a minimal row.
      merged.set(key, {
        id: key,
        login: row.login,
        platformId: row.platformId ?? 0,
        connector: row.connector ?? '',
        name: '',
        group: '',
        country: '',
        accCurrency: '',
        isEa: false,
        uniqueId: row.uniqueId ?? null,
        floatingPnL: row.floatingPnL,
      });
    }
  });

  let keys = Array.from(merged.keys());
  if (hasInstrumentFilter) keys = keys.filter((k) => nopKeys.has(k));
  return keys
    .map((k) => merged.get(k) as RiskDashboardClientAccountEnrichmentDto)
    .sort((a, b) => String(a.login).localeCompare(String(b.login), undefined, { numeric: true }));
}

export function ClientAccountsTable({
  rows,
  loading,
  error,
  onOpenLogin,
}: {
  rows: RiskDashboardClientAccountEnrichmentDto[];
  loading: boolean;
  error?: string | null;
  onOpenLogin: (login: number, platformId: number) => void;
}) {
  type R = RiskDashboardClientAccountEnrichmentDto;
  const columns: DataGridColumn<R>[] = [
    {
      key: 'login',
      title: 'Login',
      className: 'text-start',
      value: (r) => r.login,
      cell: (r) => (
        <span className="rd-instrument-cell">
          {r.platformId ? (
            <a
              href="#"
              className="fw-semibold"
              onClick={(e) => {
                e.preventDefault();
                onOpenLogin(r.login, r.platformId);
              }}
            >
              {r.login}
            </a>
          ) : (
            <span className="fw-semibold">{r.login}</span>
          )}
          {r.isEa && <span className="fk-scalper-badge fk-ea-badge ms-1">EA</span>}
          {r.isScalper && <span className="fk-scalper-badge ms-1">SCALPER</span>}
          <ClientLoginInfoTooltip row={r} />
        </span>
      ),
    },
    { key: 'drawdown', title: 'Max Drawdown', className: 'text-end', value: (r) => r.drawdown ?? null, cell: (r) => (r.drawdown == null ? 'N/A' : `${formatNumber(Math.abs(r.drawdown), 2)}%`) },
    { key: 'floatingPnL', title: 'Floating PL', className: 'text-end', value: (r) => r.floatingPnL ?? null, cell: (r) => pnl(r.floatingPnL) },
    { key: 'floatSwaps', title: 'Float Swaps', className: 'text-end', value: (r) => r.floatSwaps ?? null, cell: (r) => num(r.floatSwaps) },
    { key: 'realizedPl', title: 'RealizedPl', className: 'text-end', value: (r) => r.realizedPl ?? null, cell: (r) => pnl(r.realizedPl) },
    { key: 'commission', title: 'Coms', className: 'text-end', value: (r) => r.commission ?? null, cell: (r) => num(r.commission) },
    { key: 'closedSwaps', title: 'Swaps', className: 'text-end', value: (r) => r.closedSwaps ?? null, cell: (r) => num(r.closedSwaps) },
    { key: 'totalPL', title: 'Total PL', className: 'text-end', value: (r) => r.totalPL ?? null, cell: (r) => pnl(r.totalPL) },
    { key: 'ifSwaps', title: 'IfSwaps', className: 'text-end', value: (r) => r.ifSwaps ?? null, cell: (r) => num(r.ifSwaps) },
    { key: 'markUp', title: 'MarkUp', className: 'text-end', value: (r) => r.markUp ?? null, cell: (r) => num(r.markUp) },
    { key: 'pspFees', title: 'PSP Fees', className: 'text-end', value: (r) => r.pspFees ?? null, cell: (r) => num(r.pspFees) },
    { key: 'finalPnL', title: 'Final PL', className: 'text-end', value: (r) => r.finalPnL ?? null, cell: (r) => pnl(r.finalPnL) },
  ];

  return (
    <div className="card custom-card h-100 mb-0">
      <div className="card-header justify-content-center">
        <div className="card-title text-center mb-0">
          <i className="fe fe-users me-2 text-primary" />
          Client Accounts
        </div>
      </div>
      <div className="card-body p-0">
        <DataGrid
          columns={columns}
          data={loading || error ? [] : rows}
          rowKey={(r) => r.id}
          columnFilters
          emptyMessage={
            loading ? 'Loading…' : error ? <span className="text-danger">{error}</span> : (
              <>
                Click <strong>Fetch</strong> for a snapshot, or turn on <strong>Live</strong> for realtime.
              </>
            )
          }
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LP accounts (NOP live rows merged with the LP-accounts hub rows)
// ---------------------------------------------------------------------------

export interface MergedLpRow {
  key: string;
  login: number;
  platformId: number | null;
  connector: string;
  revenueShare: number | null;
  riskyVol: number | null;
  credit: number | null;
  equity: number | null;
  marginUsed: number | null;
  freeMargin: number | null;
  marginLevel: number | null;
  floatingPnL: number | null;
  breachLevel: number;
  instruments: RiskDashboardLoginDetailDto[];
}

function keyOf(platformId: number | null | undefined, login: number): string {
  return `${platformId ?? 0}:${login}`;
}

export function mergeLpRows(
  nop: RiskDashboardLpAccountRowDto[],
  hub: LpHubAccountDto[],
  platformFilter: number | null,
): MergedLpRow[] {
  const byKey = new Map<string, MergedLpRow>();

  for (const n of nop) {
    const k = keyOf(n.platformId ?? null, n.login);
    byKey.set(k, {
      key: k,
      login: n.login,
      platformId: n.platformId ?? null,
      connector: n.connector ?? '',
      revenueShare: null,
      riskyVol: null,
      credit: null,
      equity: null,
      marginUsed: null,
      freeMargin: null,
      marginLevel: null,
      floatingPnL: n.floatingPnL,
      breachLevel: 0,
      instruments: n.instruments ?? [],
    });
  }

  for (const a of hub) {
    const k = keyOf(a.platformId ?? null, a.login);
    const existing = byKey.get(k);
    const hubRow: MergedLpRow = {
      key: k,
      login: a.login,
      platformId: a.platformId ?? null,
      connector: a.platformName || a.serverName || existing?.connector || '',
      revenueShare: a.revenueShare ?? null,
      riskyVol: a.riskyVol ?? null,
      credit: a.creditRc ?? a.credit ?? null,
      equity: a.equityRc ?? a.equity ?? null,
      marginUsed: a.marginUsedRc ?? null,
      freeMargin: a.freeMarginRc ?? null,
      marginLevel: a.marginLevel ?? null,
      floatingPnL: existing?.floatingPnL ?? a.floatingRc ?? null,
      breachLevel: a.breachLevel ?? 0,
      instruments: existing?.instruments ?? [],
    };
    byKey.set(k, existing ? { ...existing, ...hubRow, instruments: existing.instruments } : hubRow);
  }

  let rows = Array.from(byKey.values());
  if (platformFilter) rows = rows.filter((r) => Number(r.platformId) === Number(platformFilter));
  return rows;
}

function marginLevelCell(value: number | null): ReactNode {
  if (value == null || Number.isNaN(value)) return <span className="text-muted fst-italic">N/A</span>;
  const cls = value < 100 ? 'text-danger' : value < 200 ? 'text-warning' : 'text-primary';
  return <span className={`fw-semibold ${cls}`}>{formatNumber(value, 2)}%</span>;
}

export function LpAccountsTable({
  nopRows,
  hubRows,
  platformFilter,
  dates,
  onOpenLogin,
  buildContributors,
}: {
  nopRows: RiskDashboardLpAccountRowDto[];
  hubRows: LpHubAccountDto[];
  platformFilter: number | null;
  dates: { startDate: string; endDate: string };
  onOpenLogin: (login: number, platformId: number) => void;
  buildContributors: (details: RiskDashboardLoginDetailDto[]) => ReactNode;
}) {
  const rows = useMemo(() => mergeLpRows(nopRows, hubRows, platformFilter), [nopRows, hubRows, platformFilter]);

  const columns: DataGridColumn<MergedLpRow>[] = [
    {
      key: 'login',
      title: 'Login',
      className: 'text-start',
      value: (r) => r.login,
      cell: (r) => (
        <span className="rd-instrument-cell">
          {r.platformId ? (
            <a
              href="#"
              className="fw-semibold"
              onClick={(e) => {
                e.preventDefault();
                onOpenLogin(r.login, r.platformId as number);
              }}
            >
              {r.login}
            </a>
          ) : (
            <span className="fw-semibold">{r.login}</span>
          )}
          <LpLoginInfoTooltip row={r} dates={dates} />
        </span>
      ),
    },
    { key: 'connector', title: 'Broker', className: 'text-start', value: (r) => r.connector },
    { key: 'revenueShare', title: 'Rev Share', className: 'text-end', value: (r) => r.revenueShare, cell: (r) => num(r.revenueShare) },
    { key: 'riskyVol', title: 'RiskyVol', className: 'text-end', value: (r) => r.riskyVol, cell: (r) => num(r.riskyVol) },
    { key: 'credit', title: 'Credit', className: 'text-end', value: (r) => r.credit, cell: (r) => num(r.credit) },
    { key: 'equity', title: 'Equity', className: 'text-end', value: (r) => r.equity, cell: (r) => num(r.equity) },
    { key: 'marginUsed', title: 'Used Mrg', className: 'text-end', value: (r) => r.marginUsed, cell: (r) => num(r.marginUsed) },
    { key: 'freeMargin', title: 'Free Mrg', className: 'text-end', value: (r) => r.freeMargin, cell: (r) => num(r.freeMargin) },
    { key: 'marginLevel', title: 'Margin Lvl', className: 'text-end', value: (r) => r.marginLevel, cell: (r) => marginLevelCell(r.marginLevel) },
    { key: 'floatingPnL', title: 'Float PnL', className: 'text-end', value: (r) => r.floatingPnL, cell: (r) => pnl(r.floatingPnL) },
  ];

  return (
    <div className="card custom-card h-100 mb-0">
      <div className="card-header justify-content-center">
        <div className="card-title text-center mb-0">
          <i className="fe fe-airplay me-2 text-primary" />
          LP Accounts
        </div>
      </div>
      <div className="card-body p-0">
        <DataGrid
          columns={columns}
          data={rows}
          rowKey={(r) => r.key}
          columnFilters
          rowClassName={(r) => (r.breachLevel > 0 ? 'threshold-breach' : undefined)}
          emptyMessage="Waiting for live LP accounts from connected terminals."
          expandable={(r) => buildContributors(r.instruments)}
        />
      </div>
    </div>
  );
}
